import { ACCOUNT_TYPES } from "@tastyworks/shared-utils/misc/constants"
import { camelCase, find, snakeCase } from "lodash-es"
import { SchemaDeSerBuilder, type SchemaDeSer, type Updater } from "./deser"
import { ArrayMap } from "./util/collection"
import type { JsonHelper, JsonKeyExtractor } from "./util/json"

export enum MARGIN_TYPES {
  MARGIN = "Margin",
  CASH = "Cash",
}

export const BUSINESS_ACCOUNT_SUBTYPES = Object.freeze({
  [ACCOUNT_TYPES.C_CORP]: ACCOUNT_TYPES.C_CORP,
  [ACCOUNT_TYPES.S_CORP]: ACCOUNT_TYPES.S_CORP,
  [ACCOUNT_TYPES.LLC]: ACCOUNT_TYPES.LLC,
  [ACCOUNT_TYPES.PARTNERSHIP]: ACCOUNT_TYPES.PARTNERSHIP,
})

export enum ACCOUNT_TYPE_CHOICES {
  INDIVIDUAL_BROKERAGE = "individualBrokerage",
  RETIREMENT = "retirement",
  JOINT = "joint",
  ENTITY = "entity",
}

export enum ENTITY_ACCOUNT_TYPE_CHOICES {
  C_CORP = "cCorp",
  S_CORP = "sCorp",
  LLC = "llc",
  PARTNERSHIP = "partnership",
  TRUST = "trust",
}

export const BUSINESS_ACCOUNT_TYPE_CHOICES = [
  ENTITY_ACCOUNT_TYPE_CHOICES.C_CORP,
  ENTITY_ACCOUNT_TYPE_CHOICES.S_CORP,
  ENTITY_ACCOUNT_TYPE_CHOICES.LLC,
  ENTITY_ACCOUNT_TYPE_CHOICES.PARTNERSHIP,
]

export const TRUST_ACCOUNT_TYPE_CHOICES = [ENTITY_ACCOUNT_TYPE_CHOICES.TRUST]

export const TAX_ELECTION_CHOICES = [
  ENTITY_ACCOUNT_TYPE_CHOICES.C_CORP,
  ENTITY_ACCOUNT_TYPE_CHOICES.S_CORP,
  ENTITY_ACCOUNT_TYPE_CHOICES.PARTNERSHIP,
] as const

export const TAX_ELECTION = {
  [ENTITY_ACCOUNT_TYPE_CHOICES.C_CORP]: "C. Corp",
  [ENTITY_ACCOUNT_TYPE_CHOICES.S_CORP]: "S. Corp",
  [ENTITY_ACCOUNT_TYPE_CHOICES.PARTNERSHIP]: "Partnership",
} as const satisfies Record<(typeof TAX_ELECTION_CHOICES)[number], string>

export type TaxElection = (typeof TAX_ELECTION)[keyof typeof TAX_ELECTION]

export const JOINT_ACCOUNT_TYPE_CHOICES = {
  JOINT_TENANTS_IN_COMMON: camelCase(ACCOUNT_TYPES.JOINT_TENANTS_IN_COMMON),
  JOINT_WROS: camelCase(ACCOUNT_TYPES.JOINT_WROS),
}

export const INTERNAL_TRANSFER_ELIGIBLE_ACCOUNT_TYPES = [
  ACCOUNT_TYPES.BENEFICIARY_ROTH_IRA,
  ACCOUNT_TYPES.BENEFICIARY_TRADITIONAL_IRA,
  ACCOUNT_TYPES.INDIVIDUAL,
  ACCOUNT_TYPES.ROTH_IRA,
  ACCOUNT_TYPES.TRADITIONAL_IRA,
  ACCOUNT_TYPES.SEP_IRA,
]

export enum RETIREMENT_ACCOUNT_CHOICES {
  INDIVIDUAL = "individual",
  BENEFICIARY_INHERITED = "beneficiary / Inherited",
}

export enum JOINT_ACCOUNT_CHOICES {
  JOINT_TENANTS_IN_COMMON = "jointTenantsInCommon",
  JOINT_WROS = "jointTenantsWithRightsOfSurvivorship",
}

export enum IRA_INDIVIDUAL_ACCOUNT_TYPE_CHOICES {
  ROTH_IRA = "rothIra",
  TRADITIONAL_IRA = "traditionalIra",
  SEP_IRA = "sep",
}

export enum IRA_BENEFICIARY_OR_INHERITED_ACCOUNT_TYPE_CHOICES {
  BENEFICIARY_TRADITIONAL_IRA = "beneficiaryTraditionalIra",
  BENEFICIARY_ROTH_IRA = "beneficiaryRothIra",
}

export const RETIREMENT_ACCOUNT_TYPES = [
  ACCOUNT_TYPES.BENEFICIARY_ROTH_IRA,
  ACCOUNT_TYPES.BENEFICIARY_TRADITIONAL_IRA,
  ACCOUNT_TYPES.ROTH_IRA,
  ACCOUNT_TYPES.SEP_IRA,
  ACCOUNT_TYPES.TRADITIONAL_IRA,
]

export const IRA_INDIVIDUAL = [
  ACCOUNT_TYPES.ROTH_IRA,
  ACCOUNT_TYPES.TRADITIONAL_IRA,
  ACCOUNT_TYPES.SEP_IRA,
]

export const ROTH_IRA_ACCOUNT_TYPES = [
  ACCOUNT_TYPES.ROTH_IRA,
  ACCOUNT_TYPES.BENEFICIARY_ROTH_IRA,
]

export const TRADITIONAL_IRA_ACCOUNT_TYPES = [
  ACCOUNT_TYPES.TRADITIONAL_IRA,
  ACCOUNT_TYPES.BENEFICIARY_TRADITIONAL_IRA,
]

export const BENEFICIARY_ACCOUNT_TYPES = [
  ACCOUNT_TYPES.BENEFICIARY_TRADITIONAL_IRA,
  ACCOUNT_TYPES.BENEFICIARY_ROTH_IRA,
]

export const JOINT_ACCOUNT_TYPES = [
  ACCOUNT_TYPES.JOINT_TENANTS_IN_COMMON,
  ACCOUNT_TYPES.JOINT_WROS,
]

export const ENTITY_ACCOUNT_TYPES = [
  ACCOUNT_TYPES.BUSINESS,
  ACCOUNT_TYPES.TRUST,
]

export class MarginType {
  name = ""
  isMargin = false
}

export const MARGIN_TYPE_DESER: SchemaDeSer<MarginType> =
  new SchemaDeSerBuilder(MarginType)
    .ofString("name")
    .ofBoolean("isMargin")
    .toDeSer()

export class AccountType {
  name = ""
  description = ""
  isTaxAdvantaged = false
  hasMultipleOwners = false
  isPubliclyAvailable = false
  marginTypes: MarginType[] = []
}

export const ACCOUNT_TYPE_DESER: SchemaDeSer<AccountType> =
  new SchemaDeSerBuilder(AccountType)
    .ofString("name")
    .ofString("description")
    .ofBoolean("isTaxAdvantaged")
    .ofBoolean("hasMultipleOwners")
    .ofBoolean("isPubliclyAvailable")
    .ofArray("marginTypes", MARGIN_TYPE_DESER, MarginType)
    .toDeSer()

export const ACCOUNT_TYPE_PARSER = ACCOUNT_TYPE_DESER.toParser(AccountType)

export const ACCOUNT_TYPE_UPDATER = ACCOUNT_TYPE_DESER.update

export const ACCOUNT_TYPE_KEY_EXTRACTOR = (helper: JsonHelper) =>
  helper.getString("name")

type ALL_ACCOUNT_TYPES = ACCOUNT_TYPES | MARGIN_TYPES

export class PermittedAccountType {
  name: ALL_ACCOUNT_TYPES = ACCOUNT_TYPES.INDIVIDUAL
  reason = ""
  isPermitted = false
  subTypes: PermittedAccountTypeMap = createPermittedAccountTypeMap()
}

export class PermittedAccountTypeCollection extends Array<PermittedAccountType> {}

export type PermittedAccountTypeMap = ArrayMap<string, PermittedAccountType>

export const PERMITTED_ACCOUNT_TYPE_KEY_EXTRACTOR: JsonKeyExtractor<
  ALL_ACCOUNT_TYPES
> = (helper) => {
  const value = helper.getString("name")
  let rv: ALL_ACCOUNT_TYPES | undefined = find(
    ACCOUNT_TYPES,
    (v) => value === snakeCase(v)
  )
  if (rv == null) {
    rv = find(BUSINESS_ACCOUNT_SUBTYPES, (v) => value === snakeCase(v))
  }
  if (rv == null) {
    rv = find(MARGIN_TYPES, (v) => value === snakeCase(v))
  }
  return rv!
}

export function createPermittedAccountTypeMap() {
  return ArrayMap.stringKey(PermittedAccountType, "name")
}

export const PERMITTED_ACCOUNT_TYPE_UPDATER: Updater<PermittedAccountType> = (
  target,
  helper
) => {
  target.name = PERMITTED_ACCOUNT_TYPE_KEY_EXTRACTOR(helper)
  target.reason = helper.getString("reason")
  if (helper.hasField("sub-types")) {
    helper.updateArrayMap(
      "sub-types",
      target.subTypes,
      PERMITTED_ACCOUNT_TYPE_UPDATER,
      PERMITTED_ACCOUNT_TYPE_KEY_EXTRACTOR
    )
    target.isPermitted = target.subTypes.values.some(
      (subType) => subType.isPermitted
    )
  } else {
    target.isPermitted = helper.getBoolean("is-permitted")
  }
}

export function findDeepPermittedAccountTypeByKey(
  map: PermittedAccountTypeMap,
  key: string
) {
  let result: PermittedAccountType | undefined

  function searchRecursively(subMap: PermittedAccountTypeMap) {
    const permittedAccountType = subMap.findByKey(key)
    if (permittedAccountType) {
      result = permittedAccountType
    }

    for (const permAcctType of subMap.values) {
      if (result) {
        return result
      }
      searchRecursively(permAcctType.subTypes)
    }

    return result
  }

  return searchRecursively(map)
}

export type AccountTypeChoiceValue =
  | ACCOUNT_TYPE_CHOICES.INDIVIDUAL_BROKERAGE
  | ACCOUNT_TYPE_CHOICES.RETIREMENT
  | ACCOUNT_TYPE_CHOICES.JOINT
  | ACCOUNT_TYPE_CHOICES.ENTITY
  | JOINT_ACCOUNT_CHOICES.JOINT_TENANTS_IN_COMMON
  | JOINT_ACCOUNT_CHOICES.JOINT_WROS
  | IRA_INDIVIDUAL_ACCOUNT_TYPE_CHOICES.ROTH_IRA
  | IRA_INDIVIDUAL_ACCOUNT_TYPE_CHOICES.TRADITIONAL_IRA
  | IRA_INDIVIDUAL_ACCOUNT_TYPE_CHOICES.SEP_IRA
  | IRA_BENEFICIARY_OR_INHERITED_ACCOUNT_TYPE_CHOICES.BENEFICIARY_ROTH_IRA
  | IRA_BENEFICIARY_OR_INHERITED_ACCOUNT_TYPE_CHOICES.BENEFICIARY_TRADITIONAL_IRA
  | ENTITY_ACCOUNT_TYPE_CHOICES.C_CORP
  | ENTITY_ACCOUNT_TYPE_CHOICES.S_CORP
  | ENTITY_ACCOUNT_TYPE_CHOICES.LLC
  | ENTITY_ACCOUNT_TYPE_CHOICES.PARTNERSHIP
  | ENTITY_ACCOUNT_TYPE_CHOICES.TRUST

export const choiceToAccountTypeMapping: Record<string, ACCOUNT_TYPES> =
  Object.freeze({
    [ACCOUNT_TYPE_CHOICES.INDIVIDUAL_BROKERAGE]: ACCOUNT_TYPES.INDIVIDUAL,
    [JOINT_ACCOUNT_CHOICES.JOINT_TENANTS_IN_COMMON]:
      ACCOUNT_TYPES.JOINT_TENANTS_IN_COMMON,
    [JOINT_ACCOUNT_CHOICES.JOINT_WROS]: ACCOUNT_TYPES.JOINT_WROS,
    [IRA_INDIVIDUAL_ACCOUNT_TYPE_CHOICES.ROTH_IRA]: ACCOUNT_TYPES.ROTH_IRA,
    [IRA_INDIVIDUAL_ACCOUNT_TYPE_CHOICES.TRADITIONAL_IRA]:
      ACCOUNT_TYPES.TRADITIONAL_IRA,
    [IRA_INDIVIDUAL_ACCOUNT_TYPE_CHOICES.SEP_IRA]: ACCOUNT_TYPES.SEP_IRA,
    [IRA_BENEFICIARY_OR_INHERITED_ACCOUNT_TYPE_CHOICES.BENEFICIARY_ROTH_IRA]:
      ACCOUNT_TYPES.BENEFICIARY_ROTH_IRA,
    [IRA_BENEFICIARY_OR_INHERITED_ACCOUNT_TYPE_CHOICES.BENEFICIARY_TRADITIONAL_IRA]:
      ACCOUNT_TYPES.BENEFICIARY_TRADITIONAL_IRA,

    [ENTITY_ACCOUNT_TYPE_CHOICES.C_CORP]: ACCOUNT_TYPES.C_CORP,
    [ENTITY_ACCOUNT_TYPE_CHOICES.S_CORP]: ACCOUNT_TYPES.S_CORP,
    [ENTITY_ACCOUNT_TYPE_CHOICES.LLC]: ACCOUNT_TYPES.LLC,
    [ENTITY_ACCOUNT_TYPE_CHOICES.PARTNERSHIP]: ACCOUNT_TYPES.PARTNERSHIP,

    [ENTITY_ACCOUNT_TYPE_CHOICES.TRUST]: ACCOUNT_TYPES.TRUST,
  })

export const accountTypeToChoiceMapping = Object.freeze(
  Object.fromEntries(
    Object.entries(choiceToAccountTypeMapping).map(([k, v]) => [v, k])
  )
) as Record<ACCOUNT_TYPES, string>
